/**
 * Boost Commerce Bundles — shared vanilla JS module.
 *
 * Provides:
 *   BoostBundles.fetch(productId)       → Promise<bundle|null>
 *   BoostBundles.hasBundle(productId)    → Promise<boolean>
 *   BoostBundles.checkBatch(ids)         → Promise<Map<id, boolean>>
 *   BoostBundles.renderInline(id, el)    → renders FBT section
 *   BoostBundles.openModal(productId)    → opens bundle modal
 */
;(function () {
  'use strict'

  var SHOP = window.__instantSearchShop || (document.querySelector('[data-shop-domain]') || {}).dataset?.shopDomain || ''
  var API = 'https://services.mybcapps.com/bc-sf-filter/bundles'
  var CACHE_TTL = 5 * 60 * 1000
  var MAX_CONCURRENT = 4

  /* ──────────────────── Cache ──────────────────── */
  var cache = {}

  function getCached(id) {
    var entry = cache[id]
    if (entry && Date.now() - entry.ts < CACHE_TTL) return entry.data
    return undefined
  }

  function setCache(id, data) {
    cache[id] = { data: data, ts: Date.now() }
  }

  /* ──────────────────── API ──────────────────── */
  function fetchBundle(productId) {
    var cached = getCached(productId)
    if (cached !== undefined) return Promise.resolve(cached)

    return fetch(API + '?shop=' + encodeURIComponent(SHOP) + '&product_id=' + productId)
      .then(function (res) { return res.json() })
      .then(function (data) {
        var bundle = (data.bundles && data.bundles.length > 0) ? data.bundles[0] : null
        setCache(productId, bundle)
        return bundle
      })
      .catch(function () {
        setCache(productId, null)
        return null
      })
  }

  function hasBundle(productId) {
    return fetchBundle(productId).then(function (b) { return b !== null })
  }

  function checkBatch(ids) {
    var results = new Map()
    var queue = ids.slice()
    var running = 0

    return new Promise(function (resolve) {
      function next() {
        if (queue.length === 0 && running === 0) return resolve(results)
        while (running < MAX_CONCURRENT && queue.length > 0) {
          var id = queue.shift()
          running++
          hasBundle(id).then(function (id) {
            return function (has) {
              results.set(id, has)
              running--
              next()
            }
          }(id))
        }
      }
      next()
    })
  }

  /* ──────────────────── Helpers ──────────────────── */
  function formatPrice(amount) {
    var num = parseFloat(amount)
    if (isNaN(num)) return '£0.00'
    return '£' + num.toFixed(2)
  }

  function thumbUrl(src, size) {
    if (!src) return ''
    size = size || 200
    // Shopify CDN resize
    return src.replace(/(\.[a-z]+)(\?|$)/, '_' + size + 'x$1$2')
  }

  function getFirstImage(product) {
    if (product.images_info && product.images_info[0]) return product.images_info[0].src
    if (product.images) {
      var keys = Object.keys(product.images)
      if (keys.length > 0) return product.images[keys[0]]
    }
    return ''
  }

  function getVariantId(product) {
    if (product.variants && product.variants[0]) return product.variants[0].id
    return null
  }

  /* ──────────────────── Cart ──────────────────── */
  function addToCart(items) {
    var cartDrawer = document.querySelector('cart-drawer')
    var sectionsToRender = []
    if (cartDrawer && cartDrawer.getSectionsToRender) {
      sectionsToRender = cartDrawer.getSectionsToRender().map(function (s) { return s.id })
    }
    if (cartDrawer && cartDrawer.getIconsToRender) {
      sectionsToRender = sectionsToRender.concat(cartDrawer.getIconsToRender().map(function (s) { return s.id }))
    }

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: items,
        sections: sectionsToRender,
        sections_url: window.location.pathname
      })
    })
      .then(function (res) { return res.json() })
      .then(function (response) {
        if (response.status) {
          // Error
          console.error('[BoostBundles] Cart error:', response.description)
          return response
        }
        if (cartDrawer && cartDrawer.renderContents) {
          cartDrawer.renderContents(response)
        }
        // Publish cart update event if available
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'boost-bundles' })
        }
        return response
      })
  }

  /* ──────────────────── Scroll Lock ──────────────────── */
  var savedScrollY = 0

  function lockScroll() {
    savedScrollY = window.scrollY
    document.body.style.position = 'fixed'
    document.body.style.top = '-' + savedScrollY + 'px'
    document.body.style.left = '0'
    document.body.style.right = '0'
  }

  function unlockScroll() {
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.left = ''
    document.body.style.right = ''
    window.scrollTo(0, savedScrollY)
  }

  /* ──────────────────── Product Row HTML ──────────────────── */
  function buildProductRow(product, checked, isFirst) {
    var img = getFirstImage(product)
    var price = product.price_min != null ? product.price_min : (product.variants && product.variants[0] ? parseFloat(product.variants[0].price) : 0)
    var variantId = getVariantId(product)
    var handle = product.handle || ''

    return '<label class="boost-bundle__product' + (isFirst ? ' boost-bundle__product--primary' : '') + '">' +
      '<input type="checkbox" class="boost-bundle__checkbox" data-variant-id="' + variantId + '" data-price="' + price + '"' + (checked ? ' checked' : '') + (isFirst ? ' disabled' : '') + '>' +
      '<span class="boost-bundle__product-image">' +
        (img ? '<img src="' + thumbUrl(img, 120) + '" alt="' + (product.title || '').replace(/"/g, '&quot;') + '" loading="lazy">' : '') +
      '</span>' +
      '<span class="boost-bundle__product-info">' +
        '<a href="/products/' + handle + '" class="boost-bundle__product-title">' + (product.title || '') + '</a>' +
        '<span class="boost-bundle__product-price">' + formatPrice(price) + '</span>' +
      '</span>' +
    '</label>'
  }

  /* ──────────────────── Recalc Total ──────────────────── */
  function recalcTotal(container) {
    var total = 0
    var count = 0
    var checkboxes = container.querySelectorAll('.boost-bundle__checkbox')
    for (var i = 0; i < checkboxes.length; i++) {
      if (checkboxes[i].checked) {
        total += parseFloat(checkboxes[i].getAttribute('data-price')) || 0
        count++
      }
    }
    var totalEl = container.querySelector('.boost-bundle__total-price')
    if (totalEl) totalEl.textContent = formatPrice(total)
    var countEl = container.querySelector('.boost-bundle__item-count')
    if (countEl) countEl.textContent = count + ' item' + (count !== 1 ? 's' : '')
    var btn = container.querySelector('.boost-bundle__add-btn')
    if (btn) btn.disabled = count === 0
  }

  function getSelectedItems(container) {
    var items = []
    var checkboxes = container.querySelectorAll('.boost-bundle__checkbox:checked')
    for (var i = 0; i < checkboxes.length; i++) {
      var vid = checkboxes[i].getAttribute('data-variant-id')
      if (vid) items.push({ id: parseInt(vid, 10), quantity: 1 })
    }
    return items
  }

  /* ──────────────────── Inline FBT (Product Page) ──────────────────── */
  function renderInline(productId, container) {
    if (!container) return
    container.innerHTML = '<div class="boost-bundle__loading"><span class="boost-bundle__spinner"></span></div>'

    fetchBundle(productId).then(function (bundle) {
      if (!bundle || !bundle.products || bundle.products.length < 2) {
        container.innerHTML = ''
        container.style.display = 'none'
        return
      }

      var products = bundle.products.slice(0, bundle.bundle_limit || 4)
      var html = '<div class="boost-bundle boost-bundle--inline">' +
        '<h3 class="boost-bundle__heading">Frequently Bought Together</h3>' +
        '<div class="boost-bundle__products">'

      for (var i = 0; i < products.length; i++) {
        html += buildProductRow(products[i], true, i === 0)
      }

      html += '</div>' +
        '<div class="boost-bundle__footer">' +
          '<div class="boost-bundle__total">' +
            '<span class="boost-bundle__item-count">' + products.length + ' items</span>' +
            '<span class="boost-bundle__total-price">' + formatPrice(products.reduce(function (s, p) { return s + (p.price_min || 0) }, 0)) + '</span>' +
          '</div>' +
          '<button class="boost-bundle__add-btn" type="button">Add Bundle to Cart</button>' +
        '</div>' +
      '</div>'

      container.innerHTML = html

      // Wire up checkbox changes
      var checkboxes = container.querySelectorAll('.boost-bundle__checkbox')
      for (var j = 0; j < checkboxes.length; j++) {
        checkboxes[j].addEventListener('change', function () { recalcTotal(container) })
      }

      // Wire up add to cart
      var addBtn = container.querySelector('.boost-bundle__add-btn')
      if (addBtn) {
        addBtn.addEventListener('click', function () {
          var items = getSelectedItems(container)
          if (items.length === 0) return
          addBtn.disabled = true
          addBtn.textContent = 'Adding...'
          addToCart(items).then(function () {
            addBtn.disabled = false
            addBtn.textContent = 'Add Bundle to Cart'
          }).catch(function () {
            addBtn.disabled = false
            addBtn.textContent = 'Add Bundle to Cart'
          })
        })
      }
    })
  }

  /* ──────────────────── Modal ──────────────────── */
  var modalEl = null
  var modalOverlay = null

  function createModal() {
    if (modalEl) return

    modalOverlay = document.createElement('div')
    modalOverlay.className = 'boost-bundle-modal-overlay'
    modalOverlay.addEventListener('click', closeModal)
    document.body.appendChild(modalOverlay)

    modalEl = document.createElement('div')
    modalEl.className = 'boost-bundle-modal'
    modalEl.setAttribute('role', 'dialog')
    modalEl.setAttribute('aria-modal', 'true')
    document.body.appendChild(modalEl)
  }

  function openModal(productId) {
    createModal()
    modalEl.innerHTML = '<div class="boost-bundle-modal__inner"><div class="boost-bundle__loading"><span class="boost-bundle__spinner"></span></div></div>'
    modalEl.classList.add('is-open')
    modalOverlay.classList.add('is-open')
    lockScroll()

    // Close on escape
    document.addEventListener('keydown', handleEscape)

    fetchBundle(productId).then(function (bundle) {
      if (!bundle || !bundle.products || bundle.products.length < 2) {
        modalEl.innerHTML = '<div class="boost-bundle-modal__inner">' +
          '<button class="boost-bundle-modal__close" type="button" aria-label="Close">&times;</button>' +
          '<p class="boost-bundle__empty">No bundle available for this product.</p>' +
        '</div>'
        wireCloseBtn()
        return
      }

      var products = bundle.products.slice(0, bundle.bundle_limit || 4)
      var html = '<div class="boost-bundle-modal__inner">' +
        '<button class="boost-bundle-modal__close" type="button" aria-label="Close">&times;</button>' +
        '<h3 class="boost-bundle__heading">Frequently Bought Together</h3>' +
        '<div class="boost-bundle__products">'

      for (var i = 0; i < products.length; i++) {
        html += buildProductRow(products[i], true, i === 0)
      }

      html += '</div>' +
        '<div class="boost-bundle__footer">' +
          '<div class="boost-bundle__total">' +
            '<span class="boost-bundle__item-count">' + products.length + ' items</span>' +
            '<span class="boost-bundle__total-price">' + formatPrice(products.reduce(function (s, p) { return s + (p.price_min || 0) }, 0)) + '</span>' +
          '</div>' +
          '<button class="boost-bundle__add-btn" type="button">Add Bundle to Cart</button>' +
        '</div>' +
      '</div>'

      modalEl.innerHTML = html
      wireCloseBtn()

      // Wire checkboxes
      var checkboxes = modalEl.querySelectorAll('.boost-bundle__checkbox')
      for (var j = 0; j < checkboxes.length; j++) {
        checkboxes[j].addEventListener('change', function () { recalcTotal(modalEl) })
      }

      // Wire add to cart
      var addBtn = modalEl.querySelector('.boost-bundle__add-btn')
      if (addBtn) {
        addBtn.addEventListener('click', function () {
          var items = getSelectedItems(modalEl)
          if (items.length === 0) return
          addBtn.disabled = true
          addBtn.textContent = 'Adding...'
          addToCart(items).then(function () {
            addBtn.disabled = false
            addBtn.textContent = 'Added!'
            setTimeout(function () {
              closeModal()
            }, 600)
          }).catch(function () {
            addBtn.disabled = false
            addBtn.textContent = 'Add Bundle to Cart'
          })
        })
      }
    })
  }

  function wireCloseBtn() {
    var btn = modalEl.querySelector('.boost-bundle-modal__close')
    if (btn) btn.addEventListener('click', closeModal)
  }

  function closeModal() {
    if (modalEl) modalEl.classList.remove('is-open')
    if (modalOverlay) modalOverlay.classList.remove('is-open')
    unlockScroll()
    document.removeEventListener('keydown', handleEscape)
  }

  function handleEscape(e) {
    if (e.key === 'Escape') closeModal()
  }

  /* ──────────────────── Init (product page) ──────────────────── */
  function init() {
    var containers = document.querySelectorAll('[data-boost-fbt]')
    for (var i = 0; i < containers.length; i++) {
      var productId = containers[i].getAttribute('data-product-id')
      if (productId) renderInline(productId, containers[i])
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }

  /* ──────────────────── Public API ──────────────────── */
  window.BoostBundles = {
    fetch: fetchBundle,
    hasBundle: hasBundle,
    checkBatch: checkBatch,
    renderInline: renderInline,
    openModal: openModal,
    closeModal: closeModal,
    addToCart: addToCart
  }
})()
